/**
 * The matrix cell key: one source paired with one target.
 *
 * ISSUE-068. The format `<sourceId>:<targetId>` was written inline in three
 * places, matched by SUFFIX in two more, and parsed in a sixth — six sites
 * that had to agree and nothing making them. Two of the matches were unsafe:
 * `key.endsWith(targetId)` matches a cell it does not own whenever one target
 * id is a suffix of another (`'abc'` and `'0abc'`). The indicator half of that
 * was cosmetic; `toggleGroupCells` WRITES through the same filter, so a
 * collision silently changed which conversions were queued.
 *
 * `resource_id` is an opaque server string (`src/api/index.ts:34`) assigned
 * straight through to `FileInfo.id` (`AudioEditorPanel.tsx:518`). No format,
 * length or charset is asserted anywhere in this repository and no test fixes
 * one, so the collision cannot be ruled out — it is unconstrained, not
 * unreachable.
 *
 * The delimiter stays `:`. The backend reads these keys
 * (`postCvcMatrixGenerate` sends them as `source_resource_id` /
 * `target_resource_ids`), so changing the format is an API change that needs
 * backend agreement — explicitly out of ISSUE-068's scope.
 */

const DELIMITER = ':';

export interface CellPair {
  sourceId: string;
  targetId: string;
}

/** The one place the key format is written. */
export const cellKey = (sourceId: string, targetId: string): string =>
  `${sourceId}${DELIMITER}${targetId}`;

/**
 * Split a key back into its pair, or `null` when it is not a well-formed cell
 * key.
 *
 * Returns null rather than guessing when the key does not have EXACTLY two
 * segments. `key.split(':')` destructured to two names — what
 * `src/api/index.ts` did — silently discards a third segment, so an id
 * containing the delimiter produced a TRUNCATED `source_resource_id` and a
 * wrong `target_resource_ids`: a silently wrong conversion rather than a
 * visible failure. Ambiguity is detected here so callers can decide.
 */
export const parseCellKey = (key: string): CellPair | null => {
  const parts = key.split(DELIMITER);
  if (parts.length !== 2) return null;
  const [sourceId, targetId] = parts;
  if (!sourceId || !targetId) return null;
  return { sourceId, targetId };
};

/** Exact-match the target half. Replaces `key.endsWith(targetId)`. */
export const isCellOfTarget = (key: string, targetId: string): boolean =>
  parseCellKey(key)?.targetId === targetId;

/** Exact-match the source half. Replaces `key.startsWith(`${sourceId}:`)`. */
export const isCellOfSource = (key: string, sourceId: string): boolean =>
  parseCellKey(key)?.sourceId === sourceId;
